import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { uniqueId } from "lodash";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { compose } from "redux";

import { actions as topikActions } from "../../../../modules/topik";
import TextAreaInput from "../../../../components/form/TextAreaInput";
import CharCounter from "../../../../components/form/CharCounter";
import YesNoToggle from "../../../../components/form/YesNoToggle";
import { commitHaptic } from "../haptics";
import { useDraft, draftKey } from "../useDraft";
import AnalysisFrame from "./AnalysisFrame";
import AnalysisRecall, { chosenPairCards } from "./AnalysisRecall";
import TopikAssistPanel from "./TopikAssistPanel";

// Terminal analysis step (§6.2a c): the goal + two capability/will self-assessments. No bet.
// The prompt asks for a goal "based on this reflection", so the whole reflection so far -
// observation, risk (+ its mitigation), idea (+ its maximizer) - is carried onto the screen
// (memory-load) rather than left for the player to recall.
const TopikGoalView = ({ t, session, topikStatus, gameClosed, dispatch }) => {
  const [goal, setGoal] = useDraft(draftKey(session, "goal"), session.goal || "");
  const [goalCapability, setGoalCapability] = useState(
    typeof session.goalCapability === "boolean" ? session.goalCapability : null,
  );
  const [goalWill, setGoalWill] = useState(
    typeof session.goalWill === "boolean" ? session.goalWill : null,
  );

  // The title only praises; the goal question is in the instruction, so it names the field.
  const instructionId = useRef(uniqueId("topik-goal-instruction-")).current;

  const complete = goal.trim() && goalCapability !== null && goalWill !== null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!complete || topikStatus.submitting || gameClosed) return;
    commitHaptic();
    dispatch(topikActions.analysis.goal.request({
      goal: goal.trim(),
      goalCapability,
      goalWill,
    }));
  };

  return (
    <AnalysisFrame
      t={t}
      title={t("topik-goal-view.title")}
      instruction={t("topik-goal-view.instruction")}
      instructionId={instructionId}
      canBack
      submitting={topikStatus.submitting}
      gameClosed={gameClosed}
      error={topikStatus.error}
      onBack={() => dispatch(topikActions.analysis.back.request())}
    >
      <AnalysisRecall
        entries={[
          {
            key: "observation",
            label: t("topik-analysis.recall.observation"),
            value: session.observation,
            cards: chosenPairCards(session),
          },
          { key: "risk", label: t("topik-analysis.recall.risk"), value: session.risk },
          {
            key: "riskMitigation",
            label: t("topik-analysis.recall.risk-mitigation"),
            value: session.riskMitigation,
          },
          { key: "solution", label: t("topik-analysis.recall.solution"), value: session.solution },
          {
            key: "solutionMaximizer",
            label: t("topik-analysis.recall.solution-maximizer"),
            value: session.solutionMaximizer,
          },
        ]}
      />
      <form className="topik-view__form" onSubmit={handleSubmit}>
        <div className="topik-analysis__textarea-wrapper">
          <TextAreaInput
            className="topik-textarea"
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            placeholder={t("topik-goal-view.placeholder")}
            minRows={2}
            maxLength={session.maxTextLength}
            inputProps={{
              disabled: topikStatus.submitting,
              "aria-labelledby": instructionId,
            }}
          />
          <CharCounter value={goal} maxLength={session.maxTextLength} nudge={t("topik-view.counter-nudge")} />
        </div>

        {/* The graduated AI companion (§6.5): a nudge first, ideas only on the second press. */}
        <TopikAssistPanel step="goal" draft={goal} />

        <div className="topik-analysis__self-assessment">
          <div className="topik-analysis__assessment-row">
            <span className="topik-analysis__assessment-label">
              {t("topik-goal-view.capability-label")}
            </span>
            <YesNoToggle
              value={goalCapability}
              onChange={setGoalCapability}
              yesLabel={t("topik-goal-view.yes")}
              noLabel={t("topik-goal-view.no")}
              ariaLabel={t("topik-goal-view.capability-label")}
              disabled={topikStatus.submitting}
            />
          </div>
          <div className="topik-analysis__assessment-row">
            <span className="topik-analysis__assessment-label">
              {t("topik-goal-view.will-label")}
            </span>
            <YesNoToggle
              value={goalWill}
              onChange={setGoalWill}
              yesLabel={t("topik-goal-view.yes")}
              noLabel={t("topik-goal-view.no")}
              ariaLabel={t("topik-goal-view.will-label")}
              disabled={topikStatus.submitting}
            />
          </div>
        </div>

        <button
          type="submit"
          className="topik-view__submit-button"
          disabled={!complete || topikStatus.submitting || gameClosed}
        >
          {t("topik-goal-view.submit")}
        </button>
      </form>
    </AnalysisFrame>
  );
};

TopikGoalView.propTypes = {
  t: PropTypes.func.isRequired,
  session: PropTypes.object.isRequired,
  topikStatus: PropTypes.object.isRequired,
  gameClosed: PropTypes.bool,
  dispatch: PropTypes.func.isRequired,
};

TopikGoalView.defaultProps = {
  gameClosed: false,
};

const mapStateToProps = (state) => ({
  session: state.topik.session,
  topikStatus: state.topik.status,
});

export default compose(withTranslation("views"), connect(mapStateToProps))(TopikGoalView);
